class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onUpsellClick = this.handleUpsellClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('.cart-notification__close')?.addEventListener('click', this.close.bind(this));
    this.querySelector('.ppnw-cart-continue')?.addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onUpsellClick);
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
    this.setupPorchUpsell();
    this.dispatchCartViewEvent();
  }

  getStylingVariantMap() {
    return new Map([
      [44417117257775, 44417117519919],
      [44417117290543, 44417117552687],
      [44417117323311, 44417117585455],
      [44417117356079, 44417117618223],
      [44417117388847, 44417117650991],
      [44417117421615, 44417117683759],
      [44417117454383, 44417117716527],
      [44417117487151, 44417117749295],
      [44417141178415, 44417141440559],
      [44417141211183, 44417141473327],
      [44417141243951, 44417141506095],
      [44417141276719, 44417141538863],
      [44417141309487, 44417141571631],
      [44417141342255, 44417141604399],
      [44417141375023, 44417141637167],
      [44417141407791, 44417141669935],
    ]);
  }

  async setupPorchUpsell() {
    const upsell = this.querySelector('[data-ppnw-upsell]');
    if (!upsell) return;

    const packageProductIds = new Set([
      8037518082095,
      8037523357743,
      8037524504623,
      8037527191599,
      8037629493295,
      8037632770095,
      8037638242351,
    ]);

    try {
      const response = await fetch(`${routes.cart_url}.json`, { cache: 'no-store' });
      const cart = await response.json();
      const items = cart?.items || [];
      const hasPackage = items.some((item) => packageProductIds.has(Number(item.product_id)));

      if (!hasPackage) {
        upsell.hidden = true;
        return;
      }

      let visibleOffers = 0;
      const stylingItem = upsell.querySelector('[data-ppnw-styling-item]');
      const stylingButton = stylingItem?.querySelector('[data-ppnw-styling-add]');
      const stylingMap = this.getStylingVariantMap();
      const currentItem =
        items.find((item) => item.key === this.cartItemKey) ||
        items.find((item) => stylingMap.has(Number(item.variant_id)));

      if (stylingItem && stylingButton && currentItem) {
        const targetVariantId = stylingMap.get(Number(currentItem.variant_id));

        if (targetVariantId) {
          stylingButton.dataset.currentLineKey = currentItem.key;
          stylingButton.dataset.targetVariantId = String(targetVariantId);
          stylingButton.dataset.quantity = String(currentItem.quantity || 1);
          stylingButton.disabled = false;
          stylingButton.textContent = '+ Add Styling';

          const image = stylingItem.querySelector('[data-ppnw-styling-image]');
          const placeholder = stylingItem.querySelector('[data-ppnw-styling-placeholder]');
          if (image && currentItem.image) {
            image.src = currentItem.image;
            image.hidden = false;
            if (placeholder) placeholder.hidden = true;
          } else {
            if (image) image.hidden = true;
            if (placeholder) placeholder.hidden = false;
          }

          stylingItem.hidden = false;
          visibleOffers += 1;
        } else {
          stylingItem.hidden = true;
        }
      } else if (stylingItem) {
        stylingItem.hidden = true;
      }

      const variantsInCart = new Set(items.map((item) => Number(item.variant_id)));

      upsell.querySelectorAll('[data-ppnw-upsell-item]').forEach((item) => {
        const button = item.querySelector('[data-ppnw-upsell-add]');
        if (!button) return;
        const variantId = Number(button.dataset.variantId);
        const alreadyInCart = variantsInCart.has(variantId);
        item.hidden = alreadyInCart;
        if (!alreadyInCart) visibleOffers += 1;
      });

      upsell.hidden = visibleOffers === 0;
    } catch (error) {
      upsell.hidden = true;
    }
  }

  async handleUpsellClick(event) {
    const stylingButton = event.target.closest('[data-ppnw-styling-add]');
    if (stylingButton && this.contains(stylingButton)) {
      event.preventDefault();
      event.stopPropagation();
      await this.handleStylingUpgrade(stylingButton);
      return;
    }

    const button = event.target.closest('[data-ppnw-upsell-add]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();
    if (button.disabled) return;

    const item = button.closest('[data-ppnw-upsell-item]');
    const status = this.querySelector('[data-ppnw-upsell-status]');
    const originalText = button.textContent;
    const variantId = Number(button.dataset.variantId);

    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: ['cart-icon-bubble', 'cart-notification-button'],
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add item.');

      this.updateCartChrome(data.sections);
      button.textContent = 'Added ✓';
      if (status) status.textContent = 'Add-on added to your cart.';

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'ppnw-cart-upsell',
        productVariantId: String(variantId),
        cartData: data,
      });

      setTimeout(() => {
        if (item) item.hidden = true;
        this.setupPorchUpsell();
      }, 650);
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      if (status) status.textContent = error.message || 'Unable to add that item. Please try again.';
    }
  }

  async handleStylingUpgrade(button) {
    if (button.disabled) return;

    const status = this.querySelector('[data-ppnw-upsell-status]');
    const originalText = button.textContent;
    const currentLineKey = button.dataset.currentLineKey;
    const targetVariantId = Number(button.dataset.targetVariantId);
    const quantity = Number(button.dataset.quantity) || 1;

    if (!currentLineKey || !targetVariantId) return;

    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const addResponse = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: targetVariantId, quantity }],
          sections: ['cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const addData = await addResponse.json();

      if (!addResponse.ok || addData.status) {
        throw new Error(addData.description || addData.message || 'Unable to add styling.');
      }

      const addedKey = addData.key || addData.items?.[0]?.key;
      if (!addedKey) throw new Error('Unable to confirm the styling upgrade.');

      const changeResponse = await fetch(routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: currentLineKey,
          quantity: 0,
          sections: ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const changeData = await changeResponse.json();

      if (!changeResponse.ok || changeData.status) {
        throw new Error(changeData.description || changeData.message || 'Unable to finish the styling upgrade.');
      }

      this.cartItemKey = addedKey;

      const productHtml = changeData.sections?.['cart-notification-product'];
      const productTarget = document.getElementById('cart-notification-product');
      if (productTarget && productHtml) {
        productTarget.innerHTML = this.getSectionInnerHTML(
          productHtml,
          `[id="cart-notification-product-${addedKey}"]`
        );
      }

      this.updateCartChrome(changeData.sections);
      button.textContent = 'Styling Added ✓';
      if (status) status.textContent = 'Professional styling added to your package.';

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'ppnw-styling-upgrade',
        productVariantId: String(targetVariantId),
        cartData: changeData,
      });

      await this.setupPorchUpsell();
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      if (status) status.textContent = error.message || 'Unable to add styling. Please try again.';
    }
  }

  updateCartChrome(sections = {}) {
    if (sections?.['cart-icon-bubble']) {
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (cartIcon) cartIcon.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble']);
    }

    if (sections?.['cart-notification-button']) {
      const viewCartButton = document.getElementById('cart-notification-button');
      if (viewCartButton) viewCartButton.innerHTML = this.getSectionInnerHTML(sections['cart-notification-button']);
    }
  }

  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {}
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product', selector: `[id="cart-notification-product-${this.cartItemKey}"]` },
      { id: 'cart-notification-button' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector)?.innerHTML || '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
